import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '@/api/client';
import { api } from '@/api/client';
import { queryKeys } from '@/api/hooks';
import { useEnvironment } from '@/environment/EnvironmentProvider';
import { ErrorState, PageHeader } from '@/components/states';
import type { AppSummary } from '@/types/api';

/**
 * The product catalogue, read-only until the kill-switch is enforced anywhere.
 *
 * The switches are disabled rather than merely dimmed, and that is the whole
 * point of this page's current state. `/access/me` already answers `app_disabled`
 * correctly — the resolver has always been right — but no external app acts on
 * it: `sep-multiload` ships the check in observe mode (`ACCESS_ENFORCE=false`,
 * doc 17) where it logs a verdict and denies nobody, and the others never ask.
 * A switch that looks like a kill-switch and silently cuts nothing is worse than
 * no switch at all, because the moment it matters is an incident, and the admin
 * who flips it will believe access is gone.
 *
 * Re-enabling is deliberately one constant: drop `NOT_ENFORCED` and the page
 * works as before. The `PATCH /apps/{key}` endpoint was never disabled — this is
 * a UI gate over a working API, not a removed capability.
 */
const NOT_ENFORCED = true;

export function AppsPage() {
  const environment = useEnvironment();
  const queryClient = useQueryClient();

  const { data, isPending, error, refetch } = useQuery<AppSummary[], ApiError>({
    queryKey: queryKeys.apps,
    queryFn: ({ signal }) => api.get<AppSummary[]>('/apps', signal),
  });

  const setEnabled = useMutation<AppSummary, ApiError, { key: string; isEnabled: boolean }>({
    mutationFn: ({ key, isEnabled }) => api.patch<AppSummary>(`/apps/${key}`, { isEnabled }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.apps });
      void queryClient.invalidateQueries({ queryKey: queryKeys.matrix(environment) });
    },
  });

  return (
    <Box>
      <PageHeader
        title="Apps"
        description="Der Produktkatalog. «Freigegeben» ist als Kill-Switch gedacht: eine deaktivierte App verweigert allen Benutzern den Zugriff, ohne Lizenzen zu löschen."
      />

      {NOT_ENFORCED && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Noch nicht implementiert</AlertTitle>
          Der Kill-Switch wird von den externen Apps noch nicht durchgesetzt.
          SEP.multiload wertet die Freigabe zwar aus, läuft aber im
          Beobachtungsmodus und sperrt niemanden; die übrigen Apps fragen den
          Zugriff gar nicht ab. Ein Deaktivieren würde hier also angezeigt, aber
          keinen Benutzer aussperren — deshalb sind die Schalter gesperrt.
        </Alert>
      )}

      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {isPending ? (
        <Skeleton variant="rectangular" height={320} />
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          // Dimmed as a signal, disabled as the actual guarantee — opacity alone
          // would still let someone flip a switch that does nothing.
          sx={{ overflowX: 'auto', opacity: NOT_ENFORCED ? 0.6 : 1 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>App</TableCell>
                <TableCell>Schlüssel</TableCell>
                <TableCell>Beschreibung</TableCell>
                <TableCell align="center">Freigegeben</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map((app) => (
                <TableRow key={app.key} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Typography variant="body2">{app.name}</Typography>
                      {!app.isEnabled && <Chip label="bald" variant="outlined" />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {app.key}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {app.description ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={
                        NOT_ENFORCED
                          ? 'Noch nicht implementiert: Die externen Apps setzen die Freigabe derzeit nicht durch.'
                          : app.isEnabled
                            ? 'Deaktivieren sperrt die App für alle Mandanten — Lizenzen bleiben erhalten.'
                            : 'Freigeben stellt den vorherigen Stand aller Lizenzen wieder her.'
                      }
                    >
                      {/* A disabled control fires no events, so the tooltip needs
                          a wrapper that still receives them. */}
                      <span>
                        <Switch
                          size="small"
                          checked={app.isEnabled}
                          disabled={NOT_ENFORCED}
                          slotProps={{ input: { 'aria-label': `${app.name} freigeben` } }}
                          onChange={(event) =>
                            setEnabled.mutate({ key: app.key, isEnabled: event.target.checked })
                          }
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
